import { motion } from 'framer-motion';
import Logo from '@/assets/svg/Logo';
import { cn } from '@/lib/utils';

const SIZE = {
    lg: { w: '120', h: '63', ring: 'h-[7.5rem] w-[7.5rem]', ringInner: 'h-24 w-24' },
    md: { w: '96', h: '50', ring: 'h-28 w-28', ringInner: 'h-[5.25rem] w-[5.25rem]' },
    sm: { w: '72', h: '38', ring: 'h-[4.5rem] w-[4.5rem]', ringInner: 'h-16 w-16' },
} as const;

export type LogoLoadingScreenProps = {
    /** Full viewport overlay (route lazy load) vs in-page block */
    variant?: 'fullscreen' | 'embedded';
    /** Controls logo and halo scale */
    size?: keyof typeof SIZE;
    className?: string;
    /** Optional line under the logo (pass `t('…')` from parent) */
    message?: string;
};

/**
 * Branded loading state: MRNware logo with soft halo and motion.
 * Use `fullscreen` for Suspense fallbacks; `embedded` for section loading (e.g. checkout).
 */
export function LogoLoadingScreen({
    variant = 'fullscreen',
    size: sizeProp,
    className,
    message,
}: LogoLoadingScreenProps) {
    const size = sizeProp ?? (variant === 'fullscreen' ? 'lg' : 'md');
    const s = SIZE[size];

    const content = (
        <div
            className="flex flex-col items-center justify-center gap-5"
            role="status"
            aria-live="polite"
            aria-busy="true"
        >
            <div className="relative grid place-items-center">
                <motion.span
                    className={cn(
                        'col-start-1 row-start-1 rounded-full border border-primery-blue-200/70',
                        s.ring,
                    )}
                    animate={{ scale: [1, 1.08, 1], opacity: [0.45, 0.75, 0.45] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.span
                    className={cn(
                        'col-start-1 row-start-1 rounded-full border-2 border-primery-blue-400/25',
                        s.ringInner,
                    )}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
                />
                <motion.div
                    className="relative z-10 col-start-1 row-start-1"
                    animate={{ opacity: [0.88, 1, 0.88] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                >
                    <Logo width={s.w} height={s.h} className="drop-shadow-[0_4px_12px_rgba(20,93,160,0.18)]" />
                </motion.div>
            </div>
            {message ? (
                <p className="max-w-xs text-center text-sm font-medium text-[#5D6B82] font-cairo">{message}</p>
            ) : null}
            <div className="flex gap-1.5" aria-hidden>
                {[0, 1, 2].map((i) => (
                    <motion.span
                        key={i}
                        className="h-2 w-2 rounded-full bg-primery-blue-600"
                        animate={{ y: [0, -6, 0], opacity: [0.5, 1, 0.5] }}
                        transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            delay: i * 0.12,
                            ease: 'easeInOut',
                        }}
                    />
                ))}
            </div>
        </div>
    );

    if (variant === 'fullscreen') {
        return (
            <div
                className={cn(
                    'fixed inset-0 z-[99999] flex items-center justify-center',
                    'bg-gradient-to-b from-white via-[#F4F9FF] to-tertiary-blue-50',
                    className,
                )}
            >
                {content}
            </div>
        );
    }

    return (
        <div
            className={cn(
                'flex w-full items-center justify-center rounded-2xl border border-[#E7EDF4] bg-gradient-to-b from-[#F8FAFD] to-white py-14',
                size === 'sm' ? 'min-h-[200px] py-10' : 'min-h-[280px]',
                className,
            )}
        >
            {content}
        </div>
    );
}
